import { z } from "zod";
import { DATE_PRESETS, LEAD_SOURCES, LEAD_STATUSES, SITE_EVENT_TYPES } from "../enums";
import type { BlockType, DomainStatus, LeadSource, LeadStatus, SiteEventType, SiteStatus } from "../enums";
import { orderAddressSchema, orderItemInputSchema } from "./orders";

// Site & landing (WP7): sites, pages made of typed blocks, templates, forms and leads, custom domains, pixels and
// server side events, SEO. Every block type has its own validated props; text is plain text (escaped by the
// renderer), links and media are restricted to safe protocols and an allowlist of video providers.

const id = z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant invalide");
const text = (max: number) => z.string().trim().max(max, `${max} caractères maximum`);

/** Links accepted in blocks: http(s), mailto:, tel:, in-page anchors and site relative paths. */
export function isSafeSiteLink(value: string): boolean {
  const v = value.trim();
  if (!v) return true;
  if (/^#[A-Za-z0-9_-]{1,80}$/.test(v)) return true;
  if (/^\/(?!\/)[^\s<>"']*$/.test(v)) return true;
  if (/^(mailto|tel):[^\s<>"']{1,200}$/i.test(v)) return true;
  try {
    const url = new URL(v);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/** Media URLs: absolute http(s) only (uploads are served with absolute URLs). */
export function isSafeMediaUrl(value: string): boolean {
  const v = value.trim();
  if (!v) return true;
  try {
    const url = new URL(v);
    return (url.protocol === "http:" || url.protocol === "https:") && !/[\s<>"']/.test(v);
  } catch {
    return false;
  }
}

const mediaUrl = z
  .string()
  .trim()
  .max(1000, "URL trop longue")
  .refine(isSafeMediaUrl, "Adresse d'image invalide (http ou https)")
  .default("");
const linkUrl = z.string().trim().max(1000, "Lien trop long").refine(isSafeSiteLink, "Lien invalide").default("");
const hexColor = z.string().trim().regex(/^#[0-9a-fA-F]{6}$/, "Couleur invalide (format #RRGGBB)");

// ---------------------------------------------------------------------------------------------------------------
// Video allowlist

export type SiteVideoEmbed =
  | { provider: "youtube"; id: string }
  | { provider: "vimeo"; id: string }
  | { provider: "tiktok"; id: string }
  | { provider: "mp4"; src: string };

/** Accepts only YouTube, Vimeo, TikTok and direct MP4 links; anything else is rejected. */
export function parseSiteVideoUrl(raw: string | null | undefined): SiteVideoEmbed | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const host = url.hostname.toLowerCase().replace(/^(www\.|m\.)/, "");
  const yt = (value: string | null | undefined) => (value && /^[A-Za-z0-9_-]{11}$/.test(value) ? value : null);
  if (host === "youtu.be") {
    const vid = yt(url.pathname.slice(1).split("/")[0]);
    return vid ? { provider: "youtube", id: vid } : null;
  }
  if (host === "youtube.com" || host === "youtube-nocookie.com" || host === "music.youtube.com") {
    if (url.pathname === "/watch") {
      const vid = yt(url.searchParams.get("v"));
      return vid ? { provider: "youtube", id: vid } : null;
    }
    const m = /^\/(?:embed|shorts|live|v)\/([^/?#]+)/.exec(url.pathname);
    const vid = yt(m?.[1]);
    return vid ? { provider: "youtube", id: vid } : null;
  }
  if (host === "vimeo.com" || host === "player.vimeo.com") {
    const m = /^\/(?:video\/)?(\d{5,12})(?:\/|$)/.exec(url.pathname);
    return m?.[1] ? { provider: "vimeo", id: m[1] } : null;
  }
  if (host === "tiktok.com") {
    const m = /\/(?:video|embed\/v2|embed)\/(\d{8,25})(?:\/|$)/.exec(url.pathname);
    return m?.[1] ? { provider: "tiktok", id: m[1] } : null;
  }
  if (/\.mp4$/i.test(url.pathname) && !/[\s<>"']/.test(raw)) return { provider: "mp4", src: url.toString() };
  return null;
}

const videoUrl = z
  .string()
  .trim()
  .max(1000, "URL trop longue")
  .refine((v) => v === "" || parseSiteVideoUrl(v) !== null, "Vidéo non prise en charge : YouTube, TikTok, Vimeo ou lien MP4")
  .default("");

// ---------------------------------------------------------------------------------------------------------------
// Blocks

export const SITE_BLOCK_BACKGROUNDS = ["default", "muted", "primary", "dark"] as const;
export type SiteBlockBackground = (typeof SITE_BLOCK_BACKGROUNDS)[number];

export const SITE_BLOCK_ICONS = [
  "check",
  "truck",
  "shield",
  "star",
  "heart",
  "clock",
  "gift",
  "leaf",
  "sparkles",
  "thumbs_up",
  "phone",
  "package",
  "credit_card",
  "refresh",
  "award",
  "lock",
] as const;
export type SiteBlockIcon = (typeof SITE_BLOCK_ICONS)[number];

export const SITE_BLOCK_ICON_LABELS: Record<SiteBlockIcon, string> = {
  check: "Coche",
  truck: "Livraison",
  shield: "Protection",
  star: "Étoile",
  heart: "Cœur",
  clock: "Horloge",
  gift: "Cadeau",
  leaf: "Naturel",
  sparkles: "Éclat",
  thumbs_up: "Pouce levé",
  phone: "Téléphone",
  package: "Colis",
  credit_card: "Paiement",
  refresh: "Échange",
  award: "Qualité",
  lock: "Sécurité",
};

export const SITE_CTA_ACTIONS = ["scroll_to_form", "link", "whatsapp"] as const;
export type SiteCtaAction = (typeof SITE_CTA_ACTIONS)[number];

const background = z.enum(SITE_BLOCK_BACKGROUNDS).default("default");
const icon = z.enum(SITE_BLOCK_ICONS).default("check");
const columns = z.number().int().min(1).max(4).default(3);

export const siteHeroPropsSchema = z.object({
  eyebrow: text(80).default(""),
  title: text(160).default(""),
  subtitle: text(400).default(""),
  imageUrl: mediaUrl,
  imageAlt: text(200).default(""),
  bullets: z.array(text(120)).max(6).default([]),
  ctaLabel: text(40).default(""),
  ctaAction: z.enum(SITE_CTA_ACTIONS).default("scroll_to_form"),
  ctaUrl: linkUrl,
  layout: z.enum(["split", "centered", "image_top"]).default("split"),
  background,
});

export const siteProductPropsSchema = z.object({
  productId: id.nullable().default(null),
  title: text(160).default(""),
  badge: text(40).default(""),
  showGallery: z.boolean().default(true),
  showDescription: z.boolean().default(true),
  showCompareAt: z.boolean().default(true),
  compareAtLabel: text(40).default("Prix de référence"),
  showVariants: z.boolean().default(true),
  showQuantityOffers: z.boolean().default(true),
  ctaLabel: text(40).default("Commander maintenant"),
  background,
});

export const siteBenefitsPropsSchema = z.object({
  title: text(160).default(""),
  subtitle: text(400).default(""),
  items: z.array(z.object({ icon, title: text(80).default(""), text: text(240).default("") })).max(8).default([]),
  columns,
  background,
});

export const siteFeaturesPropsSchema = z.object({
  title: text(160).default(""),
  subtitle: text(400).default(""),
  imageUrl: mediaUrl,
  imageAlt: text(200).default(""),
  items: z.array(z.object({ title: text(80).default(""), text: text(300).default("") })).max(10).default([]),
  layout: z.enum(["image_left", "image_right", "grid", "list"]).default("image_left"),
  background,
});

export const siteTestimonialsPropsSchema = z.object({
  title: text(160).default(""),
  subtitle: text(400).default(""),
  items: z
    .array(
      z.object({
        name: text(80).default(""),
        text: text(500).default(""),
        rating: z.number().int().min(1).max(5).nullable().default(null),
        location: text(80).default(""),
        imageUrl: mediaUrl,
      }),
    )
    .max(12)
    .default([]),
  background,
});

export const siteFaqPropsSchema = z.object({
  title: text(160).default(""),
  subtitle: text(400).default(""),
  items: z.array(z.object({ question: text(200).default(""), answer: text(1500).default("") })).max(20).default([]),
  background,
});

export const siteOrderFormPropsSchema = z.object({
  formId: id.nullable().default(null),
  productIds: z.array(id).max(12).default([]),
  title: text(120).default(""),
  subtitle: text(300).default(""),
  showOffers: z.boolean().default(true),
  showSummary: z.boolean().default(true),
  background,
});

export const siteLeadFormPropsSchema = z.object({
  formId: id.nullable().default(null),
  title: text(120).default(""),
  subtitle: text(300).default(""),
  background,
});

export const siteCountdownPropsSchema = z.object({
  title: text(120).default(""),
  endsAt: z.string().datetime({ offset: true, message: "Date de fin invalide" }).nullable().default(null),
  style: z.enum(["block", "bar"]).default("block"),
  background,
});

export const siteGalleryPropsSchema = z.object({
  title: text(160).default(""),
  images: z
    .array(z.object({ url: z.string().trim().min(1, "Image requise").max(1000).refine(isSafeMediaUrl, "Adresse d'image invalide"), alt: text(200).default("") }))
    .max(24)
    .default([]),
  columns,
  background,
});

export const siteVideoPropsSchema = z.object({
  title: text(160).default(""),
  url: videoUrl,
  posterUrl: mediaUrl,
  caption: text(200).default(""),
  background,
});

export const siteCtaPropsSchema = z.object({
  title: text(160).default(""),
  subtitle: text(300).default(""),
  buttonLabel: text(40).default(""),
  action: z.enum(SITE_CTA_ACTIONS).default("scroll_to_form"),
  url: linkUrl,
  background: z.enum(SITE_BLOCK_BACKGROUNDS).default("primary"),
});

export const siteTextPropsSchema = z.object({
  title: text(160).default(""),
  body: text(5000).default(""),
  align: z.enum(["left", "center"]).default("left"),
  background,
});

export const siteImagePropsSchema = z.object({
  url: mediaUrl,
  alt: text(200).default(""),
  caption: text(200).default(""),
  link: linkUrl,
  width: z.enum(["contained", "full"]).default("contained"),
  background,
});

export const siteTrustBadgesPropsSchema = z.object({
  items: z.array(z.object({ icon, label: text(60).default("") })).max(6).default([]),
  background,
});

export const siteBundleOfferPropsSchema = z.object({
  title: text(160).default(""),
  subtitle: text(300).default(""),
  productId: id.nullable().default(null),
  highlightQty: z.number().int().min(1).max(100).nullable().default(null),
  highlightLabel: text(40).default(""),
  background,
});

export const siteWhatsappButtonPropsSchema = z.object({
  label: text(60).default(""),
  message: text(300).default(""),
  number: text(30).default(""),
  floating: z.boolean().default(false),
});

export const siteSpacerPropsSchema = z.object({
  size: z.enum(["sm", "md", "lg", "xl"]).default("md"),
  divider: z.boolean().default(false),
});

export const siteFooterPropsSchema = z.object({
  text: text(300).default(""),
  links: z.array(z.object({ label: text(60).default(""), url: linkUrl })).max(8).default([]),
  phone: text(40).default(""),
  email: z.union([z.literal(""), z.string().trim().toLowerCase().email("Adresse e-mail invalide").max(120)]).default(""),
  background: z.enum(SITE_BLOCK_BACKGROUNDS).default("dark"),
});

/** Props schema of every block type (defaults give a valid empty block). */
export const SITE_BLOCK_PROP_SCHEMAS = {
  hero: siteHeroPropsSchema,
  product: siteProductPropsSchema,
  benefits: siteBenefitsPropsSchema,
  features: siteFeaturesPropsSchema,
  testimonials: siteTestimonialsPropsSchema,
  faq: siteFaqPropsSchema,
  order_form: siteOrderFormPropsSchema,
  lead_form: siteLeadFormPropsSchema,
  countdown: siteCountdownPropsSchema,
  gallery: siteGalleryPropsSchema,
  video: siteVideoPropsSchema,
  cta: siteCtaPropsSchema,
  text: siteTextPropsSchema,
  image: siteImagePropsSchema,
  trust_badges: siteTrustBadgesPropsSchema,
  bundle_offer: siteBundleOfferPropsSchema,
  whatsapp_button: siteWhatsappButtonPropsSchema,
  spacer: siteSpacerPropsSchema,
  footer: siteFooterPropsSchema,
} satisfies Record<BlockType, z.ZodTypeAny>;

export type SiteBlockPropsMap = { [K in BlockType]: z.infer<(typeof SITE_BLOCK_PROP_SCHEMAS)[K]> };

export const siteBlockIdSchema = z.string().regex(/^[A-Za-z0-9_-]{4,40}$/, "Identifiant de bloc invalide");

function blockVariant<T extends BlockType, S extends z.AnyZodObject>(type: T, props: S) {
  return z.object({
    id: siteBlockIdSchema,
    type: z.literal(type),
    hidden: z.boolean().default(false),
    props: props.default({} as z.input<S>),
  });
}

export const siteBlockSchema = z.discriminatedUnion("type", [
  blockVariant("hero", siteHeroPropsSchema),
  blockVariant("product", siteProductPropsSchema),
  blockVariant("benefits", siteBenefitsPropsSchema),
  blockVariant("features", siteFeaturesPropsSchema),
  blockVariant("testimonials", siteTestimonialsPropsSchema),
  blockVariant("faq", siteFaqPropsSchema),
  blockVariant("order_form", siteOrderFormPropsSchema),
  blockVariant("lead_form", siteLeadFormPropsSchema),
  blockVariant("countdown", siteCountdownPropsSchema),
  blockVariant("gallery", siteGalleryPropsSchema),
  blockVariant("video", siteVideoPropsSchema),
  blockVariant("cta", siteCtaPropsSchema),
  blockVariant("text", siteTextPropsSchema),
  blockVariant("image", siteImagePropsSchema),
  blockVariant("trust_badges", siteTrustBadgesPropsSchema),
  blockVariant("bundle_offer", siteBundleOfferPropsSchema),
  blockVariant("whatsapp_button", siteWhatsappButtonPropsSchema),
  blockVariant("spacer", siteSpacerPropsSchema),
  blockVariant("footer", siteFooterPropsSchema),
]);
export type SiteBlock = z.infer<typeof siteBlockSchema>;
export type SiteBlockOf<K extends BlockType> = Extract<SiteBlock, { type: K }>;

export const siteBlocksSchema = z
  .array(siteBlockSchema)
  .max(60, "60 blocs maximum par page")
  .refine((blocks) => new Set(blocks.map((b) => b.id)).size === blocks.length, "Identifiants de blocs en double");

/** Random block id usable in the browser and in Node. */
export function newSiteBlockId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  const raw = c?.randomUUID ? c.randomUUID().replace(/-/g, "") : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  return `b${raw.slice(0, 15)}`;
}

/** A block of the given type with its default props. */
export function createSiteBlock<K extends BlockType>(type: K, props: Partial<SiteBlockPropsMap[K]> = {}): SiteBlockOf<K> {
  const parsed = (SITE_BLOCK_PROP_SCHEMAS[type] as z.ZodTypeAny).parse(props) as SiteBlockPropsMap[K];
  return { id: newSiteBlockId(), type, hidden: false, props: parsed } as unknown as SiteBlockOf<K>;
}

// ---------------------------------------------------------------------------------------------------------------
// Sites and pages

/** Subdomains and paths a merchant can never take. */
export const SITE_RESERVED_SLUGS = [
  "www",
  "app",
  "api",
  "admin",
  "administrator",
  "mail",
  "email",
  "smtp",
  "imap",
  "pop",
  "ftp",
  "s",
  "static",
  "assets",
  "cdn",
  "media",
  "img",
  "images",
  "uploads",
  "files",
  "help",
  "support",
  "status",
  "blog",
  "docs",
  "dev",
  "staging",
  "test",
  "demo",
  "digisubs",
  "dashboard",
  "login",
  "signup",
  "auth",
  "account",
  "billing",
  "root",
  "ns1",
  "ns2",
  "webmail",
  "autodiscover",
] as const;

export const SITE_RESERVED_PAGE_SLUGS = ["s", "api", "uploads", "assets", "static", "socket-io"] as const;

export const siteSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, "3 caractères minimum")
  .max(63, "63 caractères maximum")
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, "Lettres minuscules, chiffres et tirets uniquement (sans tiret au début ni à la fin)")
  .refine((v) => !v.includes("--"), "Pas de double tiret")
  .refine((v) => !(SITE_RESERVED_SLUGS as readonly string[]).includes(v), "Cette adresse est réservée");

export const sitePageSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, "Adresse de page requise")
  .max(80, "80 caractères maximum")
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, "Lettres minuscules, chiffres et tirets uniquement")
  .refine((v) => !(SITE_RESERVED_PAGE_SLUGS as readonly string[]).includes(v), "Cette adresse est réservée");

/** Font pairs (heading + body). System font stacks only: published pages never download a font. */
export const SITE_FONTS = ["Inter", "System", "Serif", "Rounded", "Geometric", "Condensed", "Classic"] as const;
export type SiteFont = (typeof SITE_FONTS)[number];
export const SITE_FONT_LABELS: Record<SiteFont, string> = {
  Inter: "Moderne",
  System: "Système",
  Serif: "Élégante",
  Rounded: "Douce",
  Geometric: "Géométrique",
  Condensed: "Impact",
  Classic: "Classique",
};
export const SITE_FONT_DESCRIPTIONS: Record<SiteFont, string> = {
  Inter: "Titres et textes nets, très lisibles",
  System: "La police de chaque appareil, la plus rapide",
  Serif: "Titres serif raffinés, textes sans serif",
  Rounded: "Lettres arrondies, accueillantes",
  Geometric: "Titres géométriques, style mode et design",
  Condensed: "Titres condensés et forts, idéal promotions",
  Classic: "Serif partout, ton traditionnel",
};

const SANS = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Noto Sans Arabic", Tahoma, sans-serif';
const SANS_UI = `ui-sans-serif, system-ui, ${SANS}`;

/** CSS font stacks of each pair, shared by the public renderer and the builder previews. */
export const SITE_FONT_STACKS: Record<SiteFont, { heading: string; body: string; headingStretch?: "condensed" }> = {
  Inter: { heading: `Inter, ${SANS_UI}`, body: `Inter, ${SANS_UI}` },
  System: { heading: `system-ui, ${SANS}`, body: `system-ui, ${SANS}` },
  Serif: { heading: '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, Cambria, "Times New Roman", "Noto Naskh Arabic", serif', body: `system-ui, ${SANS}` },
  Rounded: {
    heading: `ui-rounded, "SF Pro Rounded", "Arial Rounded MT Bold", Quicksand, Nunito, system-ui, ${SANS}`,
    body: `ui-rounded, "SF Pro Rounded", "Arial Rounded MT Bold", Quicksand, Nunito, system-ui, ${SANS}`,
  },
  Geometric: { heading: `"Avenir Next", Avenir, Montserrat, "Century Gothic", Futura, "Trebuchet MS", ${SANS_UI}`, body: `system-ui, ${SANS}` },
  Condensed: { heading: `"Arial Narrow", "Roboto Condensed", "Helvetica Neue", Bahnschrift, Oswald, Impact, ${SANS}`, body: `system-ui, ${SANS}`, headingStretch: "condensed" },
  Classic: { heading: 'Georgia, Cambria, "Times New Roman", "Noto Naskh Arabic", serif', body: 'Georgia, Cambria, "Times New Roman", "Noto Naskh Arabic", serif' },
};

/** Visual tone of a site: drives the corner radius, the default font pair and small style details of the pages. */
export const SITE_TONES = ["modern", "elegant", "warm", "bold", "minimal"] as const;
export type SiteTone = (typeof SITE_TONES)[number];
export const SITE_TONE_LABELS: Record<SiteTone, string> = {
  modern: "Moderne",
  elegant: "Élégant",
  warm: "Chaleureux",
  bold: "Audacieux",
  minimal: "Épuré",
};
export const SITE_TONE_DETAILS: Record<SiteTone, { description: string; radius: number; font: SiteFont }> = {
  modern: { description: "Net et efficace, angles adoucis", radius: 12, font: "Inter" },
  elegant: { description: "Titres raffinés, beaucoup d'espace", radius: 6, font: "Serif" },
  warm: { description: "Formes arrondies, boutons pilules", radius: 20, font: "Rounded" },
  bold: { description: "Titres forts, contrastes marqués", radius: 4, font: "Condensed" },
  minimal: { description: "Sobre, l'accent sur le produit", radius: 2, font: "Geometric" },
};

/** Page background of a brand. */
export const SITE_BACKGROUND_STYLES = ["light", "cream", "dark"] as const;
export type SiteBackgroundStyle = (typeof SITE_BACKGROUND_STYLES)[number];
export const SITE_BACKGROUND_LABELS: Record<SiteBackgroundStyle, string> = { light: "Clair", cream: "Crème", dark: "Sombre" };
export const SITE_BACKGROUND_COLORS: Record<SiteBackgroundStyle, { background: string; text: string }> = {
  light: { background: "#FFFFFF", text: "#0F172A" },
  cream: { background: "#FBF7F0", text: "#1F2937" },
  dark: { background: "#0B1220", text: "#E5E7EB" },
};

/** Ready made color pairs offered by the brand step of the builder (the merchant can pick any color too). */
export const SITE_BRAND_PALETTES: ReadonlyArray<{ key: string; name: string; primary: string; secondary: string }> = [
  { key: "confiance", name: "Bleu confiance", primary: "#2F6BFF", secondary: "#16A34A" },
  { key: "promo", name: "Rouge promo", primary: "#DC2626", secondary: "#F59E0B" },
  { key: "tech", name: "Violet tech", primary: "#7C3AED", secondary: "#06B6D4" },
  { key: "nature", name: "Vert nature", primary: "#15803D", secondary: "#CA8A04" },
  { key: "beaute", name: "Rose beauté", primary: "#BE185D", secondary: "#D97706" },
  { key: "luxe", name: "Noir luxe", primary: "#111827", secondary: "#B45309" },
  { key: "ocean", name: "Turquoise", primary: "#0F766E", secondary: "#F59E0B" },
  { key: "energie", name: "Orange énergie", primary: "#EA580C", secondary: "#2563EB" },
];

// ---------------------------------------------------------------------------------------------------------------
// Colors (pure helpers shared by the builder previews and the API theme derivation)

type SiteRgb = [number, number, number];

function hexToRgb(hex: string): SiteRgb {
  const v = /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : "#000000";
  return [parseInt(v.slice(1, 3), 16), parseInt(v.slice(3, 5), 16), parseInt(v.slice(5, 7), 16)];
}

function rgbToHex(rgb: SiteRgb): string {
  return `#${rgb.map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

function rgbToHsl([r, g, b]: SiteRgb): [number, number, number] {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = max === rn ? (gn - bn) / d + (gn < bn ? 6 : 0) : max === gn ? (bn - rn) / d + 2 : (rn - gn) / d + 4;
  h *= 60;
  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): SiteRgb {
  const hue = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] = hue < 60 ? [c, x, 0] : hue < 120 ? [x, c, 0] : hue < 180 ? [0, c, x] : hue < 240 ? [0, x, c] : hue < 300 ? [x, 0, c] : [c, 0, x];
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}

/** Relative luminance (WCAG) of a #RRGGBB color. */
export function siteColorLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as SiteRgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two #RRGGBB colors (1 to 21). */
export function siteColorContrast(a: string, b: string): number {
  const la = siteColorLuminance(a);
  const lb = siteColorLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Readable text color (white or near black) on a background color. */
export function siteInkOn(hex: string): "#FFFFFF" | "#0F172A" {
  return siteColorContrast(hex, "#FFFFFF") >= siteColorContrast(hex, "#0F172A") ? "#FFFFFF" : "#0F172A";
}

/** Secondary color suggested for a primary color (split complementary hue, balanced saturation and lightness). */
export function suggestSiteAccent(primary: string): string {
  const [h, s] = rgbToHsl(hexToRgb(primary));
  const sat = s < 0.12 ? 0.75 : Math.max(0.55, Math.min(0.85, s));
  return rgbToHex(hslToRgb(h + 150, sat, 0.45));
}

/** Initials of a brand name for the monogram logo ("Ma Boutique" gives "MB"). */
export function siteInitials(name: string): string {
  const words = name
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]}${words[1]![0]}`.toUpperCase();
}

/** Background style of a stored background color (null when it is a custom color). */
export function siteBackgroundStyleOf(backgroundColor: string | null | undefined): SiteBackgroundStyle | null {
  const v = (backgroundColor ?? "").toUpperCase();
  for (const style of SITE_BACKGROUND_STYLES) if (SITE_BACKGROUND_COLORS[style].background === v) return style;
  return null;
}

export const SITE_LANGUAGES = ["fr", "ar", "en"] as const;
export type SiteLanguage = (typeof SITE_LANGUAGES)[number];
export const SITE_LANGUAGE_LABELS: Record<SiteLanguage, string> = { fr: "Français", ar: "Arabe", en: "Anglais" };

/** Event sent to ad platforms when an order is placed on a page (Purchase is sent at confirmation, decision D17). */
export const SITE_ORDER_PLACED_EVENTS = ["lead", "purchase"] as const;
export type SiteOrderPlacedEvent = (typeof SITE_ORDER_PLACED_EVENTS)[number];

export const SITE_PAGE_TYPES = ["landing", "product", "thank_you", "policy", "custom"] as const;
export type SitePageType = (typeof SITE_PAGE_TYPES)[number];
export const SITE_PAGE_TYPE_LABELS: Record<SitePageType, string> = {
  landing: "Landing page",
  product: "Page produit",
  thank_you: "Remerciement",
  policy: "Politique",
  custom: "Personnalisée",
};

/** Monogram shown instead of a logo image: up to 3 letters or digits. */
export const siteLogoInitialsSchema = z
  .string()
  .trim()
  .max(3, "3 caractères maximum")
  .regex(/^[\p{L}\p{N}&]*$/u, "Lettres ou chiffres uniquement")
  .transform((v) => v.toUpperCase());

export const siteThemeSchema = z.object({
  primaryColor: hexColor,
  accentColor: hexColor,
  backgroundColor: hexColor,
  textColor: hexColor,
  font: z.enum(SITE_FONTS),
  radius: z.number().int().min(0, "0 minimum").max(28, "28 maximum"),
  logoUrl: z.string().trim().max(1000).refine(isSafeMediaUrl, "Adresse invalide").nullable(),
  faviconUrl: z.string().trim().max(1000).refine(isSafeMediaUrl, "Adresse invalide").nullable(),
  logoInitials: siteLogoInitialsSchema,
  tone: z.enum(SITE_TONES),
});
export type SiteThemeInput = z.infer<typeof siteThemeSchema>;

/** Brand choices of the "Créer à partir de vos couleurs" path; the API derives the full theme from them. */
export const siteBrandSchema = z.object({
  primaryColor: hexColor,
  secondaryColor: hexColor.optional(),
  background: z.enum(SITE_BACKGROUND_STYLES).optional(),
  font: z.enum(SITE_FONTS).optional(),
  tone: z.enum(SITE_TONES).optional(),
  logoUrl: z.string().trim().max(1000).refine(isSafeMediaUrl, "Adresse invalide").nullable().optional(),
  logoInitials: siteLogoInitialsSchema.optional(),
});
export type SiteBrandInput = z.infer<typeof siteBrandSchema>;

/**
 * Full theme derived from brand choices: the tone gives the radius and the default font pair, the background style
 * gives the page and text colors, a missing secondary color is suggested from the primary. Values not covered by the
 * brand (no tone chosen, favicon, a custom background) come from the base theme (template or current site theme).
 */
export function deriveSiteTheme(brand: SiteBrandInput, base: Partial<SiteThemeDto> = {}): SiteThemeDto {
  const tone: SiteTone = brand.tone ?? base.tone ?? "modern";
  const preset = SITE_TONE_DETAILS[tone];
  const bg = brand.background ? SITE_BACKGROUND_COLORS[brand.background] : { background: base.backgroundColor ?? "#FFFFFF", text: base.textColor ?? "#0F172A" };
  const primary = brand.primaryColor.toUpperCase();
  return {
    primaryColor: primary,
    accentColor: (brand.secondaryColor ?? suggestSiteAccent(primary)).toUpperCase(),
    backgroundColor: bg.background.toUpperCase(),
    textColor: bg.text.toUpperCase(),
    font: brand.font ?? (brand.tone ? preset.font : (base.font ?? preset.font)),
    radius: brand.tone ? preset.radius : (base.radius ?? preset.radius),
    logoUrl: brand.logoUrl === undefined ? (base.logoUrl ?? null) : brand.logoUrl,
    faviconUrl: base.faviconUrl ?? null,
    logoInitials: brand.logoInitials === undefined ? (base.logoInitials ?? "") : brand.logoInitials.toUpperCase(),
    tone,
  };
}

export const siteSettingsSchema = z.object({
  marketCode: z.string().trim().length(2, "Marché invalide").toUpperCase(),
  whatsappNumber: text(30),
  stickyOrderButton: z.boolean(),
  thankYouPageId: id.nullable(),
  cardPaymentProvider: z.enum(["konnect", "flouci"]).nullable(),
  language: z.enum(SITE_LANGUAGES),
  rtl: z.boolean(),
});

export const siteTrackingSchema = z.object({
  metaPixelId: z.union([z.literal(""), z.string().trim().regex(/^\d{8,20}$/, "Identifiant de pixel Meta invalide (chiffres uniquement)")]),
  metaCapiEnabled: z.boolean(),
  tiktokPixelId: z.union([z.literal(""), z.string().trim().toUpperCase().regex(/^[A-Z0-9]{10,40}$/, "Identifiant de pixel TikTok invalide")]),
  tiktokEventsApiEnabled: z.boolean(),
  gaMeasurementId: z.union([z.literal(""), z.string().trim().toUpperCase().regex(/^G-[A-Z0-9]{4,20}$/, "Identifiant GA4 invalide (format G-XXXXXXX)")]),
  gtmId: z.union([z.literal(""), z.string().trim().toUpperCase().regex(/^GTM-[A-Z0-9]{4,12}$/, "Identifiant GTM invalide (format GTM-XXXXXX)")]),
  orderPlacedEvent: z.enum(SITE_ORDER_PLACED_EVENTS),
});

export const siteSeoSchema = z.object({
  title: text(70),
  description: text(170),
  ogImageUrl: z.string().trim().max(1000).refine(isSafeMediaUrl, "Adresse d'image invalide").nullable(),
  keywords: z.array(text(40)).max(10, "10 mots-clés maximum"),
  indexable: z.boolean(),
});

export const SITE_TEMPLATE_KEYS = [
  "cod_single_product",
  "flash_offer",
  "product_launch",
  "catalog",
  "lead_contact",
  "beauty_care",
  "electronics",
  "thank_you",
  "fashion",
  "home_living",
  "preorder",
] as const;
export type SiteTemplateKey = (typeof SITE_TEMPLATE_KEYS)[number];

export const SITE_TEMPLATE_CATEGORIES = ["produit", "boutique", "lead", "promo", "utilitaire"] as const;
export type SiteTemplateCategory = (typeof SITE_TEMPLATE_CATEGORIES)[number];
export const SITE_TEMPLATE_CATEGORY_LABELS: Record<SiteTemplateCategory, string> = {
  produit: "Produit unique",
  boutique: "Boutique",
  lead: "Lead / formulaire",
  promo: "Promo",
  utilitaire: "Utilitaire",
};

export const siteCreateSchema = z.object({
  name: text(120).min(2, "Nom trop court"),
  slug: siteSlugSchema.optional(),
  templateKey: z.enum(SITE_TEMPLATE_KEYS).nullable().optional(),
  productId: id.nullable().optional(),
  /** Existing form to use in the template form block (otherwise a new form is created). */
  formId: id.nullable().optional(),
  /** Brand choices: the theme is derived from them (else the template theme is used). */
  brand: siteBrandSchema.optional(),
});
export type SiteCreateInput = z.infer<typeof siteCreateSchema>;

/** Live preview of a template with the merchant's own colors and product (creation flow). */
export const siteTemplatePreviewSchema = z.object({
  theme: siteThemeSchema.partial().optional(),
  productId: id.nullable().optional(),
  siteName: text(120).optional(),
});
export type SiteTemplatePreviewInput = z.infer<typeof siteTemplatePreviewSchema>;

export const siteUpdateSchema = z.object({
  name: text(120).min(2, "Nom trop court").optional(),
  slug: siteSlugSchema.optional(),
  theme: siteThemeSchema.partial().optional(),
  settings: siteSettingsSchema.partial().optional(),
  tracking: siteTrackingSchema.partial().optional(),
  seo: siteSeoSchema.partial().optional(),
  homePageId: id.optional(),
});
export type SiteUpdateInput = z.infer<typeof siteUpdateSchema>;

export const sitePageSeoSchema = z.object({
  title: text(70),
  description: text(170),
  ogImageUrl: z.string().trim().max(1000).refine(isSafeMediaUrl, "Adresse d'image invalide").nullable(),
  indexable: z.boolean(),
});

export const sitePageCreateSchema = z.object({
  title: text(120).min(1, "Titre requis"),
  slug: sitePageSlugSchema.optional(),
  type: z.enum(SITE_PAGE_TYPES).optional().default("landing"),
  productId: id.nullable().optional(),
  templateKey: z.enum(SITE_TEMPLATE_KEYS).nullable().optional(),
  blocks: siteBlocksSchema.optional(),
});
export type SitePageCreateInput = z.infer<typeof sitePageCreateSchema>;

export const sitePageUpdateSchema = z.object({
  title: text(120).min(1, "Titre requis").optional(),
  slug: sitePageSlugSchema.optional(),
  type: z.enum(SITE_PAGE_TYPES).optional(),
  productId: id.nullable().optional(),
  seo: sitePageSeoSchema.partial().optional(),
  blocks: siteBlocksSchema.optional(),
});
export type SitePageUpdateInput = z.infer<typeof sitePageUpdateSchema>;

/** Live preview of unsaved editor state (blocks, and optionally unsaved site style). */
export const sitePagePreviewSchema = z.object({
  title: text(120).optional(),
  productId: id.nullable().optional(),
  seo: sitePageSeoSchema.partial().optional(),
  blocks: siteBlocksSchema,
  theme: siteThemeSchema.partial().optional(),
});
export type SitePagePreviewInput = z.infer<typeof sitePagePreviewSchema>;

export const siteStatsQuery = z.object({
  preset: z.enum(["7d", "30d", "90d"]).default("30d"),
});
export type SiteStatsQuery = z.infer<typeof siteStatsQuery>;

// ---------------------------------------------------------------------------------------------------------------
// Forms and leads

export const SITE_FORM_FIELD_TYPES = ["text", "phone", "email", "textarea", "choice", "governorate", "city", "address", "number"] as const;
export type SiteFormFieldType = (typeof SITE_FORM_FIELD_TYPES)[number];
export const SITE_FORM_FIELD_TYPE_LABELS: Record<SiteFormFieldType, string> = {
  text: "Texte court",
  phone: "Téléphone",
  email: "E-mail",
  textarea: "Texte long",
  choice: "Choix",
  governorate: "Gouvernorat",
  city: "Ville / délégation",
  address: "Adresse",
  number: "Nombre",
};

/** Field types an order form must contain (with a `name` text field). */
export const SITE_ORDER_FORM_REQUIRED_TYPES: readonly SiteFormFieldType[] = ["phone", "governorate", "city", "address"];
export const SITE_SINGLE_USE_FIELD_TYPES: readonly SiteFormFieldType[] = ["phone", "email", "governorate", "city", "address"];

export const siteFormFieldSchema = z
  .object({
    key: z.string().trim().regex(/^[a-z][a-z0-9_]{0,39}$/, "Clé invalide (lettres minuscules, chiffres et _)"),
    label: text(80).min(1, "Libellé requis"),
    type: z.enum(SITE_FORM_FIELD_TYPES),
    required: z.boolean().default(false),
    options: z.array(text(80).min(1)).max(20, "20 choix maximum").default([]),
    placeholder: text(120).default(""),
  })
  .refine((f) => f.type !== "choice" || f.options.length >= 2, { message: "Ajoutez au moins deux choix", path: ["options"] });
export type SiteFormFieldInput = z.infer<typeof siteFormFieldSchema>;

export const SITE_FORM_ACTIONS = ["lead", "order"] as const;
export type SiteFormAction = (typeof SITE_FORM_ACTIONS)[number];

export const siteFormSettingsSchema = z.object({
  submitLabel: text(40).min(1, "Texte du bouton requis"),
  successMessage: text(400).min(1, "Message de confirmation requis"),
  redirectPageId: id.nullable(),
  notifyTeam: z.boolean(),
  allowQuantityOffers: z.boolean(),
});

function checkFields(fields: SiteFormFieldInput[], action: SiteFormAction, ctx: z.RefinementCtx) {
  const keys = new Set<string>();
  fields.forEach((f, i) => {
    if (keys.has(f.key)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Clé déjà utilisée", path: ["fields", i, "key"] });
    keys.add(f.key);
  });
  for (const type of SITE_SINGLE_USE_FIELD_TYPES) {
    if (fields.filter((f) => f.type === type).length > 1) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Un seul champ de type ${SITE_FORM_FIELD_TYPE_LABELS[type]}`, path: ["fields"] });
  }
  if (action === "order") {
    if (!fields.some((f) => f.key === "name")) ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Un formulaire de commande demande le nom du client (clé name)", path: ["fields"] });
    for (const type of SITE_ORDER_FORM_REQUIRED_TYPES) {
      if (!fields.some((f) => f.type === type)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Champ requis pour une commande : ${SITE_FORM_FIELD_TYPE_LABELS[type]}`, path: ["fields"] });
    }
  } else if (!fields.some((f) => f.type === "phone" || f.type === "email")) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Ajoutez un champ téléphone ou e-mail pour pouvoir recontacter le prospect", path: ["fields"] });
  }
}

export const siteFormCreateSchema = z
  .object({
    name: text(120).min(2, "Nom trop court"),
    siteId: id.nullable().optional(),
    action: z.enum(SITE_FORM_ACTIONS).optional().default("order"),
    productId: id.nullable().optional(),
    fields: z.array(siteFormFieldSchema).min(1, "Ajoutez au moins un champ").max(30, "30 champs maximum").optional(),
    settings: siteFormSettingsSchema.partial().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.fields) checkFields(v.fields, v.action, ctx);
  });
export type SiteFormCreateInput = z.infer<typeof siteFormCreateSchema>;

export const siteFormUpdateSchema = z.object({
  name: text(120).min(2, "Nom trop court").optional(),
  siteId: id.nullable().optional(),
  productId: id.nullable().optional(),
  fields: z.array(siteFormFieldSchema).min(1, "Ajoutez au moins un champ").max(30, "30 champs maximum").optional(),
  settings: siteFormSettingsSchema.partial().optional(),
});
export type SiteFormUpdateInput = z.infer<typeof siteFormUpdateSchema>;

/** Validates the fields of a form for its action (used by the API on update, where the action is stored). */
export function siteFormFieldsIssues(fields: SiteFormFieldInput[], action: SiteFormAction): Array<{ path: string; message: string }> {
  const issues: Array<{ path: string; message: string }> = [];
  const ctx = {
    addIssue: (issue: { message?: string; path?: Array<string | number> }) => issues.push({ path: (issue.path ?? []).join("."), message: issue.message ?? "Invalide" }),
    path: [],
  } as unknown as z.RefinementCtx;
  checkFields(fields, action, ctx);
  return issues;
}

export const siteFormListQuery = z.object({
  siteId: id.optional(),
  q: z.string().trim().max(120).optional(),
});
export type SiteFormListQuery = z.infer<typeof siteFormListQuery>;

export const SITE_LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  landing_form: "Landing page",
  meta_lead: "Meta Lead Ads",
  tiktok_lead: "TikTok Lead",
  website_chat: "Chat du site",
  manual: "Manuel",
};

export const siteLeadListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  q: z.string().trim().max(120).optional(),
  status: z.enum([...LEAD_STATUSES, "all"] as const).default("all"),
  source: z.enum(LEAD_SOURCES).optional(),
  formId: id.optional(),
  siteId: id.optional(),
  assigned: z.union([id, z.literal("me"), z.literal("none")]).optional(),
  preset: z.enum(DATE_PRESETS).optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
});
export type SiteLeadListQuery = z.infer<typeof siteLeadListQuery>;
export type SiteLeadListQueryInput = z.input<typeof siteLeadListQuery>;

export const siteLeadUpdateSchema = z
  .object({
    status: z.enum(LEAD_STATUSES).optional(),
    notes: text(5000).optional(),
    assignedTo: id.nullable().optional(),
  })
  .refine((v) => v.status !== undefined || v.notes !== undefined || v.assignedTo !== undefined, "Aucune modification");
export type SiteLeadUpdateInput = z.infer<typeof siteLeadUpdateSchema>;

export const siteLeadConvertSchema = z.object({
  customer: z.object({
    name: text(120).min(2, "Nom du client requis"),
    phone: z.string().trim().min(6, "Téléphone requis").max(40),
    email: z.union([z.literal(""), z.string().trim().toLowerCase().email("Adresse e-mail invalide").max(254)]).nullable().optional(),
  }),
  items: z.array(orderItemInputSchema).min(1, "Ajoutez au moins un produit").max(20, "20 lignes maximum"),
  shippingAddress: orderAddressSchema,
  customerNote: text(1000).optional().default(""),
  confirm: z.boolean().optional().default(false),
});
export type SiteLeadConvertInput = z.infer<typeof siteLeadConvertSchema>;

// ---------------------------------------------------------------------------------------------------------------
// Domains

const RESERVED_TLDS = new Set(["localhost", "local", "test", "invalid", "example", "internal", "lan", "home", "corp", "intranet", "onion", "arpa"]);

/** Normalizes a hostname typed by a merchant ("https://Boutique.TN/", "shop.example.com.") or returns null. */
export function normalizeSiteHostname(input: string): string | null {
  let v = input.trim().toLowerCase();
  v = v.replace(/^https?:\/\//, "").replace(/[/?#].*$/, "").replace(/\.$/, "");
  if (!v || v.length > 253 || v.includes(":") || v.includes("@") || /\s/.test(v)) return null;
  let host: string;
  try {
    host = new URL(`http://${v}`).hostname;
  } catch {
    return null;
  }
  const labels = host.split(".");
  if (labels.length < 2) return null;
  if (labels.every((l) => /^\d+$/.test(l))) return null;
  for (const label of labels) {
    if (!/^[a-z0-9-]{1,63}$/.test(label) || label.startsWith("-") || label.endsWith("-")) return null;
  }
  const tld = labels[labels.length - 1]!;
  if (!/^(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/.test(tld) || RESERVED_TLDS.has(tld)) return null;
  return host;
}

export const siteDomainCreateSchema = z.object({
  hostname: z
    .string()
    .trim()
    .min(4, "Domaine invalide")
    .max(260, "Domaine trop long")
    .refine((v) => normalizeSiteHostname(v) !== null, "Nom de domaine invalide (exemple : boutique.tn ou www.boutique.tn)"),
});
export type SiteDomainCreateInput = z.infer<typeof siteDomainCreateSchema>;

// ---------------------------------------------------------------------------------------------------------------
// Public endpoints (published pages)

const opt = (max: number) => z.string().trim().max(max).optional();
const publicId = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/, "Identifiant invalide");

export const siteUtmSchema = z
  .object({ source: opt(200), medium: opt(200), campaign: opt(200), content: opt(200), term: opt(200) })
  .partial()
  .default({});
export const siteClickIdsSchema = z
  .object({ fbclid: opt(500), fbc: opt(500), fbp: opt(500), ttclid: opt(500), ttp: opt(500), gclid: opt(500) })
  .partial()
  .default({});

export const sitePublicEventSchema = z.object({
  siteId: id,
  pageId: id.nullable().optional(),
  type: z.enum(SITE_EVENT_TYPES),
  eventId: z.string().regex(/^[A-Za-z0-9_.:-]{8,100}$/, "Identifiant d'événement invalide"),
  sessionId: publicId,
  visitorId: publicId,
  url: z.string().max(2000).optional().default(""),
  referrer: z.string().max(2000).optional().default(""),
  utm: siteUtmSchema,
  clickIds: siteClickIdsSchema,
  productId: id.nullable().optional(),
  variantId: id.nullable().optional(),
  qty: z.number().int().min(1).max(100).optional(),
  formId: id.nullable().optional(),
});
export type SitePublicEventInput = z.infer<typeof sitePublicEventSchema>;

export const sitePublicSubmissionSchema = z.object({
  pageId: id.nullable().optional(),
  blockId: siteBlockIdSchema.nullable().optional(),
  fields: z.record(z.string().max(40), z.union([z.string().max(2000), z.number()])).default({}),
  productId: id.nullable().optional(),
  variantId: id.nullable().optional(),
  qty: z.coerce.number().int().min(1, "Quantité minimum 1").max(100, "Quantité maximum 100").optional().default(1),
  paymentMethod: z.enum(["cod", "card"]).optional().default("cod"),
  /**
   * One id per submission generated by the page script (ObjectId shaped). The server adopts it as the lead id, so the
   * browser pixel, the direct server event and the lead.created consumer all share leadEventId(lead id).
   */
  eventId: z.string().regex(/^[a-f0-9]{24}$/i, "Identifiant d'événement invalide").optional(),
  /** Honeypot: hidden from people, filled by bots. */
  company_website: z.string().max(500).optional().default(""),
  /** Milliseconds between page load and submission, measured by the page (time trap against bots). */
  elapsedMs: z.coerce.number().int().min(0).max(86_400_000 * 30).optional(),
  sessionId: publicId.optional(),
  visitorId: publicId.optional(),
  url: z.string().max(2000).optional().default(""),
  referrer: z.string().max(2000).optional().default(""),
  utm: siteUtmSchema,
  clickIds: siteClickIdsSchema,
});
export type SitePublicSubmissionInput = z.infer<typeof sitePublicSubmissionSchema>;

export const siteTestEventSchema = z.object({
  platform: z.enum(["meta", "tiktok"]),
  testEventCode: z.string().trim().max(60).optional(),
});
export type SiteTestEventInput = z.infer<typeof siteTestEventSchema>;

export const siteEventListQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(30),
  type: z.enum(SITE_EVENT_TYPES).optional(),
  pageId: id.optional(),
});
export type SiteEventListQuery = z.infer<typeof siteEventListQuery>;

export const SITE_EVENT_TYPE_LABELS: Record<SiteEventType, string> = {
  page_view: "Vue de page",
  view_content: "Vue produit",
  add_to_cart: "Ajout au panier",
  initiate_checkout: "Début de commande",
  lead: "Lead",
  purchase: "Commande",
  contact: "Contact",
};

/** Platform event name sent for each site event. */
export const SITE_EVENT_PLATFORM_NAMES: Record<SiteEventType, string> = {
  page_view: "PageView",
  view_content: "ViewContent",
  add_to_cart: "AddToCart",
  initiate_checkout: "InitiateCheckout",
  lead: "Lead",
  purchase: "Purchase",
  contact: "Contact",
};

// ---------------------------------------------------------------------------------------------------------------
// DTOs

export interface SiteThemeDto {
  primaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  font: SiteFont;
  radius: number;
  logoUrl: string | null;
  faviconUrl: string | null;
  /** Monogram shown in the page header when there is no logo image ("" = none). */
  logoInitials: string;
  tone: SiteTone;
}

export interface SiteSettingsDto {
  currency: string;
  marketCode: string;
  whatsappNumber: string;
  stickyOrderButton: boolean;
  thankYouPageId: string | null;
  cardPaymentProvider: "konnect" | "flouci" | null;
  language: SiteLanguage;
  rtl: boolean;
}

export interface SiteTrackingDto {
  metaPixelId: string;
  metaCapiEnabled: boolean;
  tiktokPixelId: string;
  tiktokEventsApiEnabled: boolean;
  gaMeasurementId: string;
  gtmId: string;
  orderPlacedEvent: SiteOrderPlacedEvent;
}

export interface SiteSeoDto {
  title: string;
  description: string;
  ogImageUrl: string | null;
  keywords: string[];
  indexable: boolean;
}

export interface SiteUrlsDto {
  /** Always available: <API_URL>/s/<slug>. */
  path: string;
  /** https://<slug>.<SITES_BASE_DOMAIN> when a base domain is configured. */
  subdomain: string | null;
  /** https://<primary verified custom domain>. */
  primaryDomain: string | null;
  /** The address visitors should use (custom domain, then subdomain, then path). */
  live: string;
}

export interface SiteStatsSummaryDto {
  visits: number;
  leads: number;
  orders: number;
  conversionRate: number | null;
}

export interface SiteDto {
  id: string;
  name: string;
  slug: string;
  status: SiteStatus;
  templateKey: string | null;
  theme: SiteThemeDto;
  settings: SiteSettingsDto;
  tracking: SiteTrackingDto;
  seo: SiteSeoDto;
  homePageId: string | null;
  publishedAt: string | null;
  urls: SiteUrlsDto;
  primaryDomain: string | null;
  totals: { visits: number; leads: number; orders: number; revenueMinor: number };
  pagesCount: number;
  publishedPagesCount: number;
  /** The style (theme) was changed since the last publication: visitors still see the published style. */
  themeHasUnpublishedChanges: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SiteListItemDto extends SiteDto {
  last30d: SiteStatsSummaryDto;
}

export interface SitePageSeoDto {
  title: string;
  description: string;
  ogImageUrl: string | null;
  indexable: boolean;
}

export interface SitePageSummaryDto {
  id: string;
  siteId: string;
  title: string;
  slug: string;
  type: SitePageType;
  productId: string | null;
  status: "draft" | "published";
  publishedAt: string | null;
  hasUnpublishedChanges: boolean;
  isHome: boolean;
  isThankYou: boolean;
  url: string;
  blockCount: number;
  seo: SitePageSeoDto;
  stats: { views: number; conversions: number };
  createdAt: string;
  updatedAt: string;
}

export interface SitePageDto extends SitePageSummaryDto {
  blocks: SiteBlock[];
  publishedBlocks: SiteBlock[] | null;
}

export interface SiteTemplateDto {
  key: SiteTemplateKey;
  name: string;
  description: string;
  category: SiteTemplateCategory;
  /** Business sector the template is designed for ("Beauté", "Mode"...), null when generic. */
  sector: string | null;
  /** Shown first and marked "Populaire" in the gallery. */
  popular: boolean;
  pageType: SitePageType;
  needsProduct: boolean;
  /** Kind of form block of the template (null = no form). */
  formAction: SiteFormAction | null;
  blockTypes: BlockType[];
  theme: Pick<SiteThemeDto, "primaryColor" | "accentColor" | "backgroundColor" | "textColor" | "font" | "radius" | "tone">;
  highlights: string[];
}

export interface SiteLeadSummaryDto {
  total: number;
  byStatus: Record<LeadStatus, number>;
  bySource: Array<{ source: LeadSource; count: number }>;
  last30d: number;
  previous30d: number;
  /** Converted leads / all leads (null without lead). */
  conversionRate: number | null;
}

export interface SiteTrackingPlatformStatusDto {
  /** Browser pixel id set on the site. */
  pixelConfigured: boolean;
  /** Server side events switch of the site. */
  serverEnabled: boolean;
  /** Platform connection able to receive server side events (Intégrations). */
  serverConnected: boolean;
  sent7d: number;
  failed7d: number;
  skipped7d: number;
  lastSentAt: string | null;
  lastFailure: string | null;
}

export interface SiteTrackingSummaryDto {
  events7d: number;
  conversions7d: number;
  byType7d: Record<SiteEventType, number>;
  lastEventAt: string | null;
  meta: SiteTrackingPlatformStatusDto;
  tiktok: SiteTrackingPlatformStatusDto;
}

export interface SiteFormFieldDto {
  key: string;
  label: string;
  type: SiteFormFieldType;
  required: boolean;
  options: string[];
  placeholder: string;
}

export interface SiteFormDto {
  id: string;
  siteId: string | null;
  siteName: string | null;
  name: string;
  action: SiteFormAction;
  productId: string | null;
  productName: string | null;
  fields: SiteFormFieldDto[];
  settings: { submitLabel: string; successMessage: string; redirectPageId: string | null; notifyTeam: boolean; allowQuantityOffers: boolean };
  stats: { views: number; starts: number; submissions: number; conversionRate: number | null };
  usedOn: Array<{ pageId: string; title: string }>;
  leadsCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SiteLeadActivityDto {
  type: string;
  message: string;
  actorName: string | null;
  at: string;
}

export interface SiteLeadDto {
  id: string;
  source: LeadSource;
  status: LeadStatus;
  name: string;
  phone: string | null;
  email: string | null;
  formId: string | null;
  formName: string;
  siteId: string | null;
  siteName: string | null;
  pageId: string | null;
  fields: Array<{ key: string; label: string; value: string }>;
  address: { governorate: string; city: string; line1: string };
  product: { id: string; name: string; variantId: string | null; qty: number } | null;
  customerId: string | null;
  orderId: string | null;
  orderNumber: string | null;
  utm: Record<string, string>;
  clickIds: Record<string, string>;
  adId: string | null;
  campaignId: string | null;
  notes: string;
  assignedTo: { id: string; name: string } | null;
  contactedAt: string | null;
  convertedAt: string | null;
  createdAt: string;
  activity: SiteLeadActivityDto[];
}

export interface SiteLeadAssigneeDto {
  id: string;
  name: string;
  email: string;
  role: string;
}

export interface SiteDomainDto {
  id: string;
  siteId: string;
  siteName: string | null;
  hostname: string;
  status: DomainStatus;
  isPrimary: boolean;
  verifiedAt: string | null;
  lastCheckAt: string | null;
  lastError: string | null;
  checks: { txt: boolean | null; routing: boolean | null };
  instructions: {
    txtName: string;
    txtValue: string;
    cnameName: string;
    cnameTarget: string | null;
    apex: boolean;
  };
  url: string;
  createdAt: string;
}

export interface SiteEventDto {
  id: string;
  type: SiteEventType;
  eventId: string;
  pageId: string | null;
  pageTitle: string | null;
  url: string;
  device: "mobile" | "tablet" | "desktop" | "unknown";
  valueMinor: number | null;
  currency: string | null;
  clickIds: string[];
  orderId: string | null;
  leadId: string | null;
  forwarding: {
    meta: "sent" | "skipped" | "failed" | null;
    tiktok: "sent" | "skipped" | "failed" | null;
    reason: string | null;
  };
  createdAt: string;
}

export interface SiteStatsDto {
  preset: "7d" | "30d" | "90d";
  from: string;
  to: string;
  currency: string;
  totals: { visits: number; pageViews: number; leads: number; orders: number; revenueMinor: number; conversionRate: number | null };
  series: Array<{ date: string; visits: number; leads: number; orders: number }>;
  pages: Array<{ pageId: string; title: string; slug: string; visits: number; pageViews: number; leads: number; orders: number; conversionRate: number | null }>;
}

export type SiteSeoCheckStatus = "pass" | "warn" | "fail" | "info";

export interface SiteSeoCheckDto {
  key: string;
  label: string;
  status: SiteSeoCheckStatus;
  detail: string;
  recommendation: string | null;
}

export interface SiteSeoAuditDto {
  pageId: string;
  title: string;
  slug: string;
  url: string;
  status: "draft" | "published";
  score: number;
  checks: SiteSeoCheckDto[];
  meta: { title: string; description: string; ogImageUrl: string | null; indexable: boolean; h1: string | null; imageCount: number; wordCount: number };
}

export interface SiteSeoFilesDto {
  sitemapUrl: string;
  robotsUrl: string;
  sitemap: string;
  robots: string;
}

export interface SitePublicSubmissionResultDto {
  ok: true;
  kind: "lead" | "order";
  message: string;
  redirectUrl: string | null;
  orderNumber: string | null;
  totalMinor: number | null;
  currency: string | null;
  conversion: { event: "Lead" | "Purchase"; eventId: string; valueMinor: number | null; currency: string | null; contentIds: string[] } | null;
  /** True when redirectUrl is the card payment page of the merchant's gateway. */
  payment?: boolean;
}
